import { useState, type InputHTMLAttributes } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Controller,
  useForm,
  type Control,
  type SubmitHandler,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { z } from "zod";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  ApiError,
  getAdminAuthProvidersSettings,
  getAdminEmailSettings,
  sendAdminEmailTest,
  updateAdminAuthProvidersSettings,
  updateAdminEmailSettings,
} from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminAuthProvidersPayload,
  AdminEmailPayload,
  AdminSettingsPayload,
} from "@/lib/types";

/**
 * System settings cards (admin-console-split PR 5).
 *
 * The Email/Brevo, auth-provider, and settings-audit cards are platform
 * infrastructure — super-admin only, rendered by /super-admin/system. They
 * lived inside the club SettingsPage during the transition; the definitions
 * now live with the console that owns them. Their API verbs
 * (/api/admin/email/*, /api/admin/auth-providers/*, GET /api/admin/settings)
 * are require_super_admin on the server.
 */

const emailSettingsSchema = z.object({
  email_provider: z.enum(["disabled", "brevo"]),
  email_public_base_url: z.string(),
  email_from: z.string().email("Enter a valid sender email").or(z.literal("")),
  email_from_name: z.string(),
  email_brevo_api_key: z.string(),
  clear_brevo_api_key: z.boolean(),
});

type EmailSettingsValues = z.infer<typeof emailSettingsSchema>;

const emailTestSchema = z.object({
  email: z.string().email("Enter a valid recipient email"),
});

type EmailTestValues = z.infer<typeof emailTestSchema>;

const authProviderSettingsSchema = z.object({
  auth_google_client_id: z.string(),
  auth_google_client_secret: z.string(),
  clear_google_client_secret: z.boolean(),
  auth_apple_client_id: z.string(),
  auth_apple_team_id: z.string(),
  auth_apple_key_id: z.string(),
  auth_apple_private_key: z.string(),
  clear_apple_private_key: z.boolean(),
});

type AuthProviderSettingsValues = z.infer<typeof authProviderSettingsSchema>;


// ---------------------------------------------------------------------------
// Auth provider credentials
// ---------------------------------------------------------------------------

// Exported for the super-admin SystemSettingsPage — email/OAuth config is
// system-wide infra (super-admin only, apex host), not per-club. The club
// /admin/settings no longer renders these.
export function AuthProvidersCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-auth-providers"),
    queryFn: getAdminAuthProvidersSettings,
  });

  return (
    <section
      data-testid="admin-settings-auth-providers"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Auth Providers
          </h3>
          <p className="mt-1 text-[0.65rem] text-ink-muted">
            Store Google and Apple ID credentials privately for Kratos social sign-in.
          </p>
        </div>
      </header>
      {query.isLoading ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Loading…</p>
      ) : query.error ? (
        <p
          data-testid="admin-settings-auth-providers-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(query.error)}
        </p>
      ) : query.data ? (
        <AuthProvidersForm
          payload={query.data}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: unscopedKey("admin-auth-providers"),
            });
          }}
        />
      ) : null}
    </section>
  );
}

function authProviderDefaults(
  payload: AdminAuthProvidersPayload,
): AuthProviderSettingsValues {
  return {
    auth_google_client_id: payload.settings.auth_google_client_id ?? "",
    auth_google_client_secret: "",
    clear_google_client_secret: false,
    auth_apple_client_id: payload.settings.auth_apple_client_id ?? "",
    auth_apple_team_id: payload.settings.auth_apple_team_id ?? "",
    auth_apple_key_id: payload.settings.auth_apple_key_id ?? "",
    auth_apple_private_key: "",
    clear_apple_private_key: false,
  };
}

function AuthProvidersForm({
  payload,
  onSaved,
}: {
  payload: AdminAuthProvidersPayload;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<AuthProviderSettingsValues>({
    resolver: zodResolver(authProviderSettingsSchema),
    values: authProviderDefaults(payload),
  });
  const clearGoogleSecret = form.watch("clear_google_client_secret");
  const clearAppleKey = form.watch("clear_apple_private_key");
  const { settings, status } = payload;

  const save = useMutation({
    mutationFn: updateAdminAuthProvidersSettings,
    onSuccess: async (next) => {
      form.reset(authProviderDefaults(next));
      setSubmitError(null);
      setSubmitMessage("Auth provider settings saved.");
      toast.success("Auth provider settings saved");
      await onSaved();
    },
    onError: (err) => {
      setSubmitMessage(null);
      setSubmitError(formatErr(err));
      toast.error("Couldn't save auth provider settings", {
        description: formatErr(err),
      });
    },
  });

  const onSubmit: SubmitHandler<AuthProviderSettingsValues> = (values) => {
    setSubmitError(null);
    setSubmitMessage(null);
    save.mutate({
      auth_google_client_id: values.auth_google_client_id.trim(),
      ...(values.clear_google_client_secret
        ? { clear_google_client_secret: true }
        : values.auth_google_client_secret.trim()
          ? { auth_google_client_secret: values.auth_google_client_secret.trim() }
          : {}),
      auth_apple_client_id: values.auth_apple_client_id.trim(),
      auth_apple_team_id: values.auth_apple_team_id.trim(),
      auth_apple_key_id: values.auth_apple_key_id.trim(),
      ...(values.clear_apple_private_key
        ? { clear_apple_private_key: true }
        : values.auth_apple_private_key.trim()
          ? { auth_apple_private_key: values.auth_apple_private_key.trim() }
          : {}),
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-auth-providers-form"
      className="flex flex-col gap-3 text-xs"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SettingsStatusPill
          label="Google"
          value={status.google.configured ? "Ready" : "Needs setup"}
          sub={sourceLabel(status.google.client_secret_source)}
          tone={status.google.configured ? "good" : "warn"}
          testid="admin-settings-auth-google-status"
        />
        <SettingsStatusPill
          label="Apple ID"
          value={status.apple.configured ? "Ready" : "Needs setup"}
          sub={sourceLabel(status.apple.private_key_source)}
          tone={status.apple.configured ? "good" : "warn"}
          testid="admin-settings-auth-apple-status"
        />
        <SettingsStatusPill
          label="Kratos"
          value={status.restart_required ? "Restart after save" : "Live"}
          sub={status.generated_config_exists ? "generated config ready" : "save to generate config"}
          tone={status.restart_required ? "warn" : "good"}
          testid="admin-settings-auth-restart"
        />
        <SettingsStatusPill
          label="Public origin"
          value={status.public_origin || "Not set"}
          tone={status.public_origin ? "muted" : "warn"}
          testid="admin-settings-auth-origin"
        />
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="border-b border-rule px-2 py-1">
          <h4 className="font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink">
            Google
          </h4>
          <p className="mt-0.5 text-[0.6rem] font-mono text-ink-muted">
            Callback: {status.google.callback_url || "Set public origin first"}
          </p>
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Client ID"
            current={settings.auth_google_client_id || "Not set"}
            currentTestid="admin-settings-auth-google-client-id"
            testid="admin-settings-auth-google-client-id-input"
            registration={form.register("auth_google_client_id")}
            error={form.formState.errors.auth_google_client_id?.message}
            placeholder="Google OAuth client ID"
            autoComplete="off"
          />
          <InlineSettingsField
            label="New client secret"
            current={
              status.google.has_client_secret
                ? `Configured from ${status.google.client_secret_source || "settings"}`
                : "Not set"
            }
            testid="admin-settings-auth-google-secret-input"
            registration={form.register("auth_google_client_secret")}
            error={form.formState.errors.auth_google_client_secret?.message}
            placeholder={status.google.has_client_secret ? "Leave blank to keep current secret" : "Paste new client secret"}
            type="password"
            autoComplete="new-password"
            disabled={clearGoogleSecret}
          />
        </div>
        <div className="border-t border-rule p-2">
          <button
            type="button"
            onClick={() =>
              form.setValue("clear_google_client_secret", !clearGoogleSecret)
            }
            data-testid="admin-settings-auth-google-clear-secret"
            data-active={clearGoogleSecret ? "true" : undefined}
            className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
          >
            {clearGoogleSecret ? "Clear Google secret ✓" : "Clear saved Google secret"}
          </button>
        </div>
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="border-b border-rule px-2 py-1">
          <h4 className="font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink">
            Apple ID
          </h4>
          <p className="mt-0.5 text-[0.6rem] font-mono text-ink-muted">
            Callback: {status.apple.callback_url || "Set public origin first"}
          </p>
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Services ID"
            current={settings.auth_apple_client_id || "Not set"}
            currentTestid="admin-settings-auth-apple-client-id"
            testid="admin-settings-auth-apple-client-id-input"
            registration={form.register("auth_apple_client_id")}
            error={form.formState.errors.auth_apple_client_id?.message}
            placeholder="com.tktic.web"
            autoComplete="off"
          />
          <InlineSettingsField
            label="Team ID"
            current={settings.auth_apple_team_id || "Not set"}
            currentTestid="admin-settings-auth-apple-team-id"
            testid="admin-settings-auth-apple-team-id-input"
            registration={form.register("auth_apple_team_id")}
            error={form.formState.errors.auth_apple_team_id?.message}
            placeholder="Apple developer team ID"
            autoComplete="off"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Key ID"
            current={settings.auth_apple_key_id || "Not set"}
            currentTestid="admin-settings-auth-apple-key-id"
            testid="admin-settings-auth-apple-key-id-input"
            registration={form.register("auth_apple_key_id")}
            error={form.formState.errors.auth_apple_key_id?.message}
            placeholder="Apple private key ID"
            autoComplete="off"
          />
          <InlineSettingsTextArea
            label="New private key"
            current={
              status.apple.has_private_key
                ? `Configured from ${status.apple.private_key_source || "settings"}`
                : "Not set"
            }
            testid="admin-settings-auth-apple-private-key-input"
            registration={form.register("auth_apple_private_key")}
            error={form.formState.errors.auth_apple_private_key?.message}
            placeholder={status.apple.has_private_key ? "Leave blank to keep current key" : "Paste Apple private key"}
            disabled={clearAppleKey}
          />
        </div>
        <div className="border-t border-rule p-2">
          <button
            type="button"
            onClick={() => form.setValue("clear_apple_private_key", !clearAppleKey)}
            data-testid="admin-settings-auth-apple-clear-key"
            data-active={clearAppleKey ? "true" : undefined}
            className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
          >
            {clearAppleKey ? "Clear Apple key ✓" : "Clear saved Apple key"}
          </button>
        </div>
      </div>

      <p
        data-testid="admin-settings-auth-restart-hint"
        className="rounded border border-rule bg-canvas px-2 py-1.5 text-[0.65rem] text-ink-muted"
      >
        {status.restart_hint}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          data-testid="admin-settings-auth-providers-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? "Saving…" : "Save auth providers"}
        </button>
        {submitMessage ? (
          <span
            data-testid="admin-settings-auth-providers-save-status"
            className="text-[0.65rem] font-mono text-good"
          >
            {submitMessage}
          </span>
        ) : null}
        {submitError ? (
          <span
            data-testid="admin-settings-auth-providers-save-error"
            className="text-[0.65rem] font-mono text-destructive"
          >
            {submitError}
          </span>
        ) : null}
      </div>
    </form>
  );
}


// ---------------------------------------------------------------------------
// H5 / PR E — Email / Notification card (editable + test-send)
// ---------------------------------------------------------------------------

export function EmailCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-email-settings"),
    queryFn: getAdminEmailSettings,
  });

  return (
    <section
      data-testid="admin-settings-email"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Email / Notifications
          </h3>
          <p className="text-[0.65rem] text-ink-muted">
            Configure Brevo delivery and send a one-off test without exposing the stored API key.
          </p>
        </div>
      </header>
      {query.isLoading ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Loading…</p>
      ) : query.error ? (
        <p
          data-testid="admin-settings-email-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(query.error)}
        </p>
      ) : query.data ? (
        <>
          <EmailSettingsForm
            payload={query.data}
            onSaved={async () => {
              await queryClient.invalidateQueries({
                queryKey: unscopedKey("admin-email-settings"),
              });
            }}
          />
          <EmailTestForm />
        </>
      ) : null}
    </section>
  );
}

function emailDefaults(payload: AdminEmailPayload): EmailSettingsValues {
  return {
    email_provider: payload.settings.email_provider === "brevo" ? "brevo" : "disabled",
    email_public_base_url: payload.settings.email_public_base_url ?? "",
    email_from: payload.settings.email_from ?? "",
    email_from_name: payload.settings.email_from_name ?? "Tktic",
    email_brevo_api_key: "",
    clear_brevo_api_key: false,
  };
}

function EmailSettingsForm({
  payload,
  onSaved,
}: {
  payload: AdminEmailPayload;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<EmailSettingsValues>({
    resolver: zodResolver(emailSettingsSchema),
    values: emailDefaults(payload),
  });
  const clearKey = form.watch("clear_brevo_api_key");
  const { settings, status, provider, configured } = payload;

  const save = useMutation({
    mutationFn: updateAdminEmailSettings,
    onSuccess: async (next) => {
      form.reset(emailDefaults(next));
      setSubmitError(null);
      setSubmitMessage("Email settings saved.");
      toast.success("Email settings saved");
      await onSaved();
    },
    onError: (err) => {
      setSubmitMessage(null);
      setSubmitError(formatErr(err));
      toast.error("Couldn't save email settings", {
        description: formatErr(err),
      });
    },
  });

  const onSubmit: SubmitHandler<EmailSettingsValues> = (values) => {
    setSubmitError(null);
    setSubmitMessage(null);
    const body = {
      email_provider: values.email_provider,
      email_public_base_url: values.email_public_base_url.trim(),
      email_from: values.email_from.trim(),
      email_from_name: values.email_from_name.trim(),
      ...(values.clear_brevo_api_key
        ? { clear_brevo_api_key: true }
        : values.email_brevo_api_key.trim()
          ? { email_brevo_api_key: values.email_brevo_api_key.trim() }
          : {}),
    };
    save.mutate(body);
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-form"
      className="flex flex-col gap-3 text-xs"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SettingsStatusPill
          label="Provider"
          value={provider}
          sub={`source: ${status.provider_source}`}
          testid="admin-settings-email-provider"
        />
        <SettingsStatusPill
          label="Delivery"
          value={configured ? "Ready" : "Needs setup"}
          tone={configured ? "good" : "warn"}
          testid="admin-settings-email-configured"
        />
        <SettingsStatusPill
          label="Sender"
          value={status.has_from ? "Set" : "Missing"}
          tone={status.has_from ? "muted" : "warn"}
        />
        <SettingsStatusPill
          label="API key"
          value={status.has_brevo_api_key ? "Configured" : "Not set"}
          sub={
            status.has_brevo_api_key
              ? `source: ${status.brevo_api_key_source}`
              : undefined
          }
          tone={status.has_brevo_api_key ? "good" : "warn"}
          testid="admin-settings-email-brevo-key"
        />
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineEmailSelect
            label="App provider"
            current={`Current: ${provider}`}
            control={form.control}
          />
          <InlineSettingsField
            label="Public base URL"
            current={settings.email_public_base_url || "Not set"}
            currentTestid="admin-settings-email-base-url"
            testid="admin-settings-email-base-url-input"
            registration={form.register("email_public_base_url")}
            error={form.formState.errors.email_public_base_url?.message}
            placeholder="https://tktic.com"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="From address"
            current={settings.email_from || "Not set"}
            currentTestid="admin-settings-email-from"
            testid="admin-settings-email-from-input"
            registration={form.register("email_from")}
            error={form.formState.errors.email_from?.message}
            placeholder="noreply@tktic.com"
            type="email"
          />
          <InlineSettingsField
            label="From name"
            current={settings.email_from_name || "Not set"}
            currentTestid="admin-settings-email-from-name"
            testid="admin-settings-email-from-name-input"
            registration={form.register("email_from_name")}
            error={form.formState.errors.email_from_name?.message}
            placeholder="Tktic"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-[1fr_auto] md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="New Brevo API key"
            current={
              status.has_brevo_api_key
                ? `Configured from ${status.brevo_api_key_source}`
                : "Not set"
            }
            testid="admin-settings-email-brevo-key-input"
            registration={form.register("email_brevo_api_key")}
            error={form.formState.errors.email_brevo_api_key?.message}
            placeholder={status.has_brevo_api_key ? "Leave blank to keep current key" : "Paste new Brevo API key"}
            type="password"
            autoComplete="new-password"
            disabled={clearKey}
          />
          <div className="flex items-center p-2">
            <button
              type="button"
              onClick={() => form.setValue("clear_brevo_api_key", !clearKey)}
              data-testid="admin-settings-email-clear-key"
              data-active={clearKey ? "true" : undefined}
              className="w-full rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink md:w-auto"
            >
              {clearKey ? "Clear Brevo key ✓" : "Clear saved key"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          data-testid="admin-settings-email-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? "Saving…" : "Save email settings"}
        </button>
        {submitMessage ? (
          <span data-testid="admin-settings-email-save-status" className="text-[0.65rem] font-mono text-good">
            {submitMessage}
          </span>
        ) : null}
        {submitError ? (
          <span data-testid="admin-settings-email-save-error" className="text-[0.65rem] font-mono text-destructive">
            {submitError}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function SettingsStatusPill({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "muted" | "good" | "warn";
  testid?: string;
}) {
  const toneClass =
    tone === "good"
      ? "text-good"
      : tone === "warn"
        ? "text-accent"
        : "text-ink";
  return (
    <div
      data-testid={testid}
      className="rounded border border-rule bg-elevated px-2 py-1.5"
    >
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`block text-xs font-mono ${toneClass}`}>{value}</span>
      {sub ? (
        <span className="block truncate text-[0.55rem] font-mono text-ink-muted">
          {sub}
        </span>
      ) : null}
    </div>
  );
}

function sourceLabel(source: string): string | undefined {
  return source ? `source: ${source}` : undefined;
}

function InlineEmailSelect({
  label,
  current,
  control,
}: {
  label: string;
  current: string;
  control: Control<EmailSettingsValues>;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-center">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span className="block text-[0.6rem] font-mono text-ink-muted">
          {current}
        </span>
      </span>
      <Controller
        control={control}
        name="email_provider"
        render={({ field }) => (
          <TkticSelect
            value={field.value ?? "disabled"}
            onChange={field.onChange}
            data-testid="admin-settings-email-provider-input"
            ariaLabel="Email provider"
            size="sm"
            options={[
              { value: "disabled", label: "Disabled" },
              { value: "brevo", label: "Brevo" },
            ]}
          />
        )}
      />
    </label>
  );
}

function InlineSettingsField({
  label,
  current,
  currentTestid,
  testid,
  registration,
  error,
  placeholder,
  type = "text",
  autoComplete,
  inputMode,
  disabled,
}: {
  label: string;
  current: string;
  currentTestid?: string;
  testid: string;
  registration: UseFormRegisterReturn;
  error?: string;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-start">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span
          data-testid={currentTestid}
          className="block break-words text-[0.6rem] font-mono text-ink-muted"
        >
          Current: {current}
        </span>
      </span>
      <span className="flex flex-col gap-1">
        <input
          type={type}
          {...registration}
          data-testid={testid}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          disabled={disabled}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {error ? <span className="text-destructive text-[0.6rem] font-mono">{error}</span> : null}
      </span>
    </label>
  );
}

function InlineSettingsTextArea({
  label,
  current,
  currentTestid,
  testid,
  registration,
  error,
  placeholder,
  disabled,
}: {
  label: string;
  current: string;
  currentTestid?: string;
  testid: string;
  registration: UseFormRegisterReturn;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-start">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span
          data-testid={currentTestid}
          className="block break-words text-[0.6rem] font-mono text-ink-muted"
        >
          Current: {current}
        </span>
      </span>
      <span className="flex flex-col gap-1">
        <textarea
          {...registration}
          data-testid={testid}
          placeholder={placeholder}
          disabled={disabled}
          rows={5}
          className="min-h-24 rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {error ? <span className="text-destructive text-[0.6rem] font-mono">{error}</span> : null}
      </span>
    </label>
  );
}

function EmailTestForm() {
  const toast = useToast();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<EmailTestValues>({
    resolver: zodResolver(emailTestSchema),
    defaultValues: { email: "" },
  });
  const sendTest = useMutation({
    mutationFn: sendAdminEmailTest,
    onSuccess: (response) => {
      setError(null);
      setResult(
        response.ok
          ? `Test email sent${response.message_id ? ` · ${response.message_id}` : ""}`
          : `Test email ${response.status}: ${response.detail || response.provider}`,
      );
      if (response.ok) {
        toast.success("Test email sent");
      } else {
        toast.error("Couldn't send test email", {
          description: `${response.status}: ${response.detail || response.provider}`,
        });
      }
    },
    onError: (err) => {
      setResult(null);
      setError(formatErr(err));
      toast.error("Couldn't send test email", { description: formatErr(err) });
    },
  });

  const onSubmit: SubmitHandler<EmailTestValues> = (values) => {
    setResult(null);
    setError(null);
    sendTest.mutate({ email: values.email.trim() });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-test-form"
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Send test email
        </span>
        <input
          type="email"
          {...form.register("email")}
          data-testid="admin-settings-email-test-input"
          placeholder="operator@example.test"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {form.formState.errors.email?.message ? (
          <span className="text-destructive text-[0.6rem] font-mono">
            {form.formState.errors.email.message}
          </span>
        ) : null}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={sendTest.isPending}
          data-testid="admin-settings-email-test-send"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sendTest.isPending ? "Sending…" : "Send test"}
        </button>
        {result ? (
          <span data-testid="admin-settings-email-test-result" className="text-[0.65rem] font-mono text-good">
            {result}
          </span>
        ) : null}
        {error ? (
          <span data-testid="admin-settings-email-test-error" className="text-[0.65rem] font-mono text-destructive">
            {error}
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// H7 — Audit feed (controlled expander)
// ---------------------------------------------------------------------------

export function AuditCard({
  audit,
}: {
  audit: NonNullable<AdminSettingsPayload["audit"]>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="admin-settings-audit"
      data-expander-open={open ? "true" : "false"}
      className="rounded border border-rule bg-surface shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="admin-settings-audit-toggle"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm"
      >
        <span className="font-display uppercase tracking-wider text-ink">
          Settings audit
        </span>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {audit.length} {open ? "▼" : "▶"}
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3">
          {audit.length === 0 ? (
            <p
              data-testid="admin-settings-audit-empty"
              className="text-[0.6rem] font-mono text-ink-muted"
            >
              No settings changes recorded.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {audit.map((a, i) => (
                <li
                  key={`${a.timestamp}-${a.key}-${i}`}
                  data-testid="admin-settings-audit-row"
                  className="rounded border border-rule/60 bg-elevated px-2 py-1 text-[0.6rem] font-mono"
                >
                  <span className="text-ink-muted">{a.timestamp}</span>{" "}
                  <span className="text-ink">{a.key}</span>{" "}
                  <span className="text-ink-muted">by {a.actor}</span>{" "}
                  <span className="text-ink-muted">
                    · {JSON.stringify(a.old_value)} → {JSON.stringify(a.new_value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}


function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === "string") return err.detail;
    return `HTTP ${err.status}`;
  }
  return "Request failed";
}
